<script setup lang="ts">
import type { UniqueLabel } from "@lxc/app-device-common";
import { filterEmptyValues } from "@lxc/app-device-common";
import type { Ref } from "vue";
import { ref } from "vue";
import type { DeviceTypeFilter } from "~/composables/useDeviceTypeFilters";
import filtersUtils from "~/core/utils/filters.utils";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";

const { t } = useI18n();

const props = defineProps<{
  organizationCode?: number;
  label: string;
  modelValue: Dtwin[];
}>();
const emit = defineEmits(["update:modelValue"]);

const deviceName = ref("");
const deviceTypes: Ref<DeviceTypeFilter[]> = ref([]);
const deviceDeclinations: Ref<string[]> = ref([]);

const selectedDevices = computed({
  get(): Dtwin[] {
    return props.modelValue ?? [];
  },
  set(selected?: Dtwin[]) {
    emit("update:modelValue", selected);
  },
});
const isAnyAppliedFilter = computed(() => {
  return props.modelValue.length !== 0;
});

const filterTags = computed((): UniqueLabel[] => {
  return filterEmptyValues<UniqueLabel>(
    props.modelValue.map((device) =>
      filtersUtils.getDtwinTag(device, props.label),
    ),
  );
});

function deleteTagFromList(id: string) {
  if (selectedDevices.value) {
    const index = selectedDevices.value.findIndex(
      (selectedDevice) => selectedDevice.uid === id,
    );

    if (index != null && index >= 0) {
      selectedDevices.value.splice(index, 1);
    }
  }
}

function onTagDeleteClick(label: string, uid?: string) {
  deleteTagFromList(uid ?? label);
}

watch(
  () => deviceTypes.value,
  () => {
    deviceDeclinations.value = [];
  },
);
</script>

<template>
  <div
    v-if="isAnyAppliedFilter"
    class="flex items-center justify-start pt-2 pb-6"
  >
    <lxc-tag-set
      border-visible
      deletable
      type="primary"
      :data="filterTags"
      :delete-tooltip="t('filters.deleteSelectedFilter')"
      @delete="onTagDeleteClick"
    />
  </div>
  <div class="rounded-lg bg-white text-gray-900">
    <div class="flex p-6 gap-6 flex-nowrap">
      <div class="flex-1 items-start">
        <log-device-type-filter
          v-model="deviceTypes"
          :organization-code="organizationCode"
        />
      </div>
      <div class="flex-1 items-start pt-1">
        <log-device-declination-filter
          v-model="deviceDeclinations"
          :types="deviceTypes.map((type) => type.value)"
        />
      </div>
    </div>
    <div>
      <log-device-name-filter v-model="deviceName" />
      <log-device-list-filter
        v-model="selectedDevices"
        :organization-code="organizationCode"
        :name="deviceName"
        :types="deviceTypes"
        :declinations="deviceDeclinations"
      />
    </div>
  </div>
</template>
